import { Col, Row } from 'antd'

import { createAuditPersonApi } from '@eastcoal/api/auth/audit/createAuditPerson'
import { queryAuditPersonDetailByMemberIdApi } from '@eastcoal/api/auth/audit/queryAuditPersonDetailByMemberId'
import { updateAuditPersonApi } from '@eastcoal/api/auth/audit/updateAuditPerson'

import { PageContainer } from '@eastcoal/common/components/Layout/PageContainer'

import { CrudForm } from '@eastcoal/formily/components/CrudForm'
import { BackButton } from '@eastcoal/formily/components/CrudForm/shared/BackButton'
import { SaveButton } from '@eastcoal/formily/components/CrudForm/shared/SaveButton'
import { SubmitButton } from '@eastcoal/formily/components/CrudForm/shared/SubmitButton'
import { FormBlock } from '@eastcoal/formily/components/FormBlock'
import { FormItem } from '@eastcoal/formily/components/FormItem'
import { Input } from '@eastcoal/formily/components/Input'

import { useAuthStore } from '@eastcoal/auth/hooks/useStore'
import { useNavigate } from '@eastcoal/router/exports/react-router'
import { KindRootNodeCheckbox } from '@eastcoal/shared/@formily/KindRootNodeCheckbox'
import { OcrIdCard } from '@eastcoal/shared/@formily/OcrIdCard'
import { RegionCascader } from '@eastcoal/shared/@formily/RegionCascader'
import { MemberType } from '@eastcoal/shared/enums/MemberType'
import { OcrScene } from '@eastcoal/shared/enums/OcrScene'

import { AUDIT_PERSON_RESULT_PAGE_PATH } from '../constants/path'

import { AuditPersonFormExposedProps, AuditPersonFormProps } from './interface'

const schema: AuditPersonFormProps['schema'] = [
  {
    name: 'formBlock',
    type: 'void',
    'x-component': FormBlock,
    properties: {
      kindPanel: {
        title: '交易意向品类（选填）',
        type: 'void',
        'x-component': FormBlock.Panel,
        properties: {
          kinds: {
            type: 'array',
            'x-decorator': FormItem,
            'x-decorator-props': {
              wrapperWidth: 'unset'
            },
            'x-component': KindRootNodeCheckbox,
            'x-component-props': {
              multiple: true,
              memberType: MemberType.PERSON
            }
          }
        }
      },
      idCardPanel: {
        title: '个人实名信息',
        type: 'void',
        'x-component': FormBlock.Panel,
        properties: {
          ocrRow: {
            type: 'void',
            'x-component': Row,
            'x-component-props': {
              gutter: 24
            },
            properties: {
              frontOcrCol: {
                type: 'void',
                'x-component': Col,
                properties: {
                  [`{ 
                    ossId: frontOssId, 
                    result: {
                      name,
                      idNumber
                    } 
                  }`]: {
                    type: 'object',
                    'x-decorator': FormItem,
                    'x-decorator-props': {
                      wrapperWidth: 'auto'
                    },
                    'x-component': OcrIdCard.Front,
                    'x-component-props': {
                      scene: OcrScene.PERSON_AUDIT_IDCARD_FRONT
                    },
                    'x-validator': {
                      triggerType: 'onInput',
                      validator: async value => {
                        if (!value) {
                          return '请上传身份证人像面'
                        }
                        if (!value.result?.idNumber) {
                          return '请上传有效的身份证人像面'
                        }
                        return null
                      }
                    }
                  }
                }
              },
              backOcrCol: {
                type: 'void',
                'x-component': Col,
                properties: {
                  '{ossId: backOssId,result:{issueAuthority}}': {
                    type: 'object',
                    'x-decorator': FormItem,
                    'x-decorator-props': {
                      wrapperWidth: 'auto'
                    },
                    'x-component': OcrIdCard.Back,
                    'x-component-props': {
                      scene: OcrScene.PERSON_AUDIT_IDCARD_BACK
                    },
                    'x-validator': {
                      triggerType: 'onInput',
                      validator: async value => {
                        if (!value) {
                          return '请上传身份证国徽面'
                        }
                        // 签证机构为空则判无效
                        if (!value.result?.issueAuthority) {
                          return '请上传有效的身份证国徽面'
                        }
                        return null
                      }
                    }
                  }
                }
              }
            }
          },
          name: {
            title: '姓名',
            type: 'string',
            'x-decorator': FormItem,
            'x-component': Input
          },
          idNumber: {
            title: '身份证号',
            type: 'string',
            'x-decorator': FormItem,
            'x-component': Input
          }
        }
      },
      extraPanel: {
        title: '个人联系方式',
        type: 'void',
        'x-component': FormBlock.Panel,
        properties: {
          [`
          {
            province:{
              id: provinceId,
              name: provinceName
            },
            city:{
              id: cityId,
              name: cityName
            },
            district:{
              id: districtId,
              name: districtName
            }
          }
          `]: {
            title: '所属城市',
            type: 'object',
            'x-component': RegionCascader,
            'x-decorator': FormItem,
            'x-validator': [
              {
                required: true,
                message: '所属城市不能为空'
              }
            ]
          },
          address: {
            title: '详细地址',
            type: 'string',
            required: true,
            'x-component': Input,
            'x-decorator': FormItem
          },
          wechat: {
            title: '微信账号',
            type: 'string',
            'x-component': Input,
            'x-decorator': FormItem
          },
          email: {
            title: '电子邮箱',
            type: 'string',
            'x-validator': {
              format: 'email'
            },
            'x-component': Input,
            'x-decorator': FormItem
          }
        }
      }
    }
  }
]

const createApi: AuditPersonFormProps['createApi'] = (
  values,
  submitContext
) => {
  return createAuditPersonApi({
    ...values,
    formStatus: submitContext.submitType
  })
}

const updateApi: AuditPersonFormProps['updateApi'] = (
  values,
  submitContext
) => {
  return updateAuditPersonApi({
    ...values,
    formStatus: submitContext.submitType
  })
}

const readApi: AuditPersonFormProps['readApi'] = ({ memberId }) => {
  return {
    api: async () => queryAuditPersonDetailByMemberIdApi({ memberId }),
    ready: !!memberId
  }
}

export const AuditPersonForm = ({ mode }: AuditPersonFormExposedProps) => {
  const reloadAuthStore = useAuthStore(state => state.reload)
  const navigate = useNavigate()

  const handleAuditSuccess = () => {
    reloadAuthStore()
    navigate(AUDIT_PERSON_RESULT_PAGE_PATH)
  }

  return (
    <PageContainer>
      <CrudForm
        createApi={createApi}
        grid={false}
        mode={mode}
        name='auditPersonForm'
        readApi={readApi}
        schema={schema}
        updateApi={updateApi}
        actions={[
          <BackButton key='backButton' />,
          <SaveButton key='saveButton' />,
          <SubmitButton key='submitButton' />
        ]}
        layout={{
          wrapperWidth: 400
        }}
        styles={{
          body: {
            width: '896px',
            margin: '0 auto'
          }
        }}
        onSuccess={handleAuditSuccess}
      />
    </PageContainer>
  )
}
